/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    REFACTORED_COMPANY_PROFILE_FORM
 * Date:         2025-12-29
 * Modified By:  Cursor
 *
 * Description:
 * Refactored CompanyProfileForm into smaller components for 
 * maintainability and to comply with line limits. Implemented 
 * fix for optional fields deletion bug using form helpers.
 *
 * Related Screens / Modules:
 * - client/src/components/setup/company-profile/CompanyProfileForm.tsx
 */
import React from "react";
import { Form, Row, Col, Card } from "react-bootstrap";
import { useCompanyForm } from "./hooks/useCompanyForm";
import { CompanyInformation } from "./components/CompanyInformation";
import { CompanyAddress } from "./components/CompanyAddress";
import { SocialMedia } from "./components/SocialMedia";
import { CompanyServices } from "./components/CompanyServices";
import { HoursOfOperation } from "./components/HoursOfOperation";
import { Certifications } from "./components/Certifications";
import SetupBottomNavigation from "../SetupBottomNavigation";
import { useNavigate } from "react-router-dom";
import { hasErrors, touchedFromErrors, scrollToFirstError } from "../../../utils/formikErrorHelpers";

const CompanyProfileForm: React.FC = () => {
  const navigate = useNavigate();
  const { formik, profile, loading, nextSectionRef } = useCompanyForm();

  if (loading && !profile) {
    return <div>Loading company profile...</div>;
  }

  return (
    <Form onSubmit={formik.handleSubmit}>
      <Row>
        <Col>
          <div className="d-flex align-items-center mb-4">
            <h1 className="mb-0 u-H1-30">Create your company profile</h1>
          </div>
        </Col>
      </Row>
      <Row>
        {/* [2026-02-13] Maya: Col lg so columns stack on tablet, split only on desktop */}
        <Col lg={6}>
          <Card className="mb-3 border-0">
            <Card.Body>
              <Card.Title className="company-profile-title">Company information</Card.Title>
              <CompanyInformation formik={formik} />
            </Card.Body>
          </Card>
          <Card className="mb-3 border-0">
            <Card.Body>
              <Card.Title className="company-profile-title">Address information</Card.Title>
              <CompanyAddress formik={formik} />
            </Card.Body>
          </Card>
          <Card className="mb-3 border-0">
            <Card.Body>
              <Card.Title className="company-profile-title">Social media (optional)</Card.Title>
              <SocialMedia formik={formik} />
            </Card.Body>
          </Card>
        </Col>
        <Col lg={6}>
          <CompanyServices formik={formik} />
          <Card className="mb-3 border-0">
            <Card.Body>
              <Card.Title className="company-profile-title">Hours of operation</Card.Title>
              <HoursOfOperation formik={formik} />
            </Card.Body>
          </Card>
          <Card className="mb-3 border-0">
            <Card.Body>
              <Card.Title className="company-profile-title">Certifications (optional)</Card.Title>
              <Certifications formik={formik} />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <SetupBottomNavigation
        currentSection="company"
        onSkip={() => navigate("/setup/reviewcompany")}
        onSaveAndNext={async () => {
          if (formik.isSubmitting) return;

          // Validate on click; if invalid, touch fields to show errors
          const errors = await formik.validateForm();
          if (hasErrors(errors)) {
            formik.setTouched(touchedFromErrors(errors), true);
            scrollToFirstError(errors);
            return;
          }

          nextSectionRef.current = "reviewcompany";
          await formik.submitForm();
        }}
        isSubmitting={formik.isSubmitting}
        // Keep button clickable when dirty; errors are shown on click
        saveNextDisabled={!formik.dirty || formik.isSubmitting}
      />
    </Form>
  );
};

export default CompanyProfileForm;
